/**
 * Submit Feedback: a standing row at the bottom of the rail, and the dialog it
 * opens.
 *
 * During the Alpha the fastest way to find what is broken is the people using
 * it, so the way to tell the team sits one click from every screen rather than
 * behind the help menu. It asks for one thing, a message, and a kind that is
 * pre-chosen so it never stands between a person and Send.
 *
 * STORAGE. It posts to cloud-api's POST /api/v1/feedback, which keeps the
 * message against the signed-in account; admins read it on God View's
 * Feedbacks screen (screens/admin/Feedbacks.tsx). The PostHog event that
 * rides alongside carries the kind and the length, never the message: the
 * words live in one place, the database, not in two.
 *
 * Mock mode (VITE_API_MODE=mock) has no cloud-api, so the send resolves
 * without a request and the dialog behaves exactly as it does for real.
 */

import { useId, useState } from 'react';
import { Button } from '../primitives/Button';
import { Dialog } from '../primitives/Dialog';
import { FieldFrame, describedBy } from '../primitives/Field';
import { SegmentedControl } from '../patterns/SegmentedControl';
import { showToast } from '../primitives/Toast';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';
import { apiFetch, ApiError } from '../../lib/http';
import '../../styles/components/alpha-welcome.css';

type Kind = 'idea' | 'problem' | 'other';

const MAX_LENGTH = 4000;

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

async function sendFeedback(kind: Kind, message: string): Promise<void> {
  const pagePath = window.location.pathname;
  if (!USE_MOCK) {
    await apiFetch('/api/v1/feedback', {
      method: 'POST',
      body: { kind, message, page_path: pagePath },
    });
  }
  captureEvent('feedback_submitted', {
    kind,
    message_length: message.length,
    screen_path: pagePath,
  });
}

export function FeedbackButton() {
  const [open, setOpen] = useState(false);

  function openDialog() {
    captureEvent('feedback_button_clicked', { screen_path: window.location.pathname });
    setOpen(true);
  }

  return (
    <>
      <button type="button" className="feedback-row" onClick={openDialog}>
        <svg viewBox="0 0 16 16" width="15" height="15" focusable="false" aria-hidden="true">
          <path
            d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinejoin="round"
          />
        </svg>
        <span>{t('Submit Feedback')}</span>
      </button>
      {open ? <FeedbackDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const [kind, setKind] = useState<Kind>('idea');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [sending, setSending] = useState(false);
  const messageId = useId();
  const tracker = useFormTracker({
    formId: 'platform_feedback_form',
    formName: 'Submit Feedback',
    totalFields: 2,
  });

  const help = t('Anything helps: what you were trying to do, what happened, what you expected.');

  function cancel(reason: string) {
    tracker.trackCancel(reason);
    onClose();
  }

  async function submit() {
    const trimmed = message.trim();
    tracker.trackSubmit({ kind, message_length: trimmed.length });
    if (!trimmed) {
      const text = t('Write a message before sending.');
      setError(text);
      tracker.onValidationError('message', text);
      tracker.trackSubmitError('empty_message', text);
      return;
    }
    setSending(true);
    try {
      await sendFeedback(kind, trimmed.slice(0, MAX_LENGTH));
    } catch (err) {
      // The server's own sentence when it has one (the hourly cap names
      // itself), and the message stays in the box so nothing is retyped.
      const text =
        err instanceof ApiError ? err.message : t('Could not send your feedback. Try again in a moment.');
      setError(text);
      tracker.trackSubmitError(err instanceof ApiError ? err.code : 'network', text);
      setSending(false);
      return;
    }
    tracker.trackSubmitSuccess({ kind });
    showToast(t('Thanks, your feedback was sent.'), t('The team reads every message.'));
    onClose();
  }

  return (
    <Dialog
      open
      onClose={() => cancel('dismissed')}
      title={t('Submit Feedback')}
      description={t('Tell the SV Cloud team what is working and what is not.')}
      footer={
        <>
          <Button variant="ghost" onClick={() => cancel('cancel_button')}>
            {t('Cancel')}
          </Button>
          <Button variant="solid" loading={sending} onClick={submit}>
            {t('Send feedback')}
          </Button>
        </>
      }
    >
      <SegmentedControl<Kind>
        label={t('Kind of feedback')}
        value={kind}
        options={[
          { value: 'idea', label: t('Idea') },
          { value: 'problem', label: t('Problem') },
          { value: 'other', label: t('Other') },
        ]}
        onChange={(next) => {
          tracker.onFieldChange('kind', next);
          setKind(next);
        }}
      />
      <FieldFrame label={t('Message')} controlId={messageId} help={help} error={error}>
        <textarea
          id={messageId}
          className="field__input feedback__area"
          name="message"
          rows={5}
          maxLength={MAX_LENGTH}
          value={message}
          data-autofocus
          onFocus={() => tracker.onFieldFocus('message')}
          onBlur={() => tracker.onFieldBlur('message')}
          onChange={(event) => {
            setMessage(event.target.value);
            if (error) setError(undefined);
            tracker.onFieldChange('message');
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(messageId, help, error)}
        />
      </FieldFrame>
    </Dialog>
  );
}
